import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Leaf, ThermometerHot, CameraRotate, Plant, Lightning, ChartLineUp, ArrowRight, MapPin } from "@phosphor-icons/react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/9d73c555-9035-4c77-bf67-14616d2c5772/images/fa3bf0eeb313772c8eaf0a70f0c44d3d49b11f37ccfabeb3309aa77fbcd8c3fa.png";
const LEAF_DETAIL = "https://static.prod-images.emergentagent.com/jobs/9d73c555-9035-4c77-bf67-14616d2c5772/images/df0c6ed1a66bd80c280761d73fe39b3cd2d4688d3962e887a6e1f6089ee8ffa9.png";

export default function Landing() {
  return (
    <div className="min-h-screen bg-sand-50" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Greenhouse" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/35 to-sand-50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-32 grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 text-white"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/25 mb-6">
              <Leaf size={14} weight="duotone" />
              <span className="font-mono-data text-white/90">climate × phytopathology</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Diagnose your plants <em className="italic text-ochre">in the climate</em><br /> they actually live in.
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed">
              A Must For Earth pairs a fast, AI-powered photo &amp; video scan with the live weather
              your plant is breathing — heat stress, humidity, UV, drought — to deliver a
              diagnosis you can act on the same morning.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-start" className="btn-primary !bg-ochre !text-ink hover:!bg-ochre-dark hover:!text-white">
                Start a free scan <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/login" data-testid="hero-cta-signin" className="btn-secondary !text-white !border-white/60 hover:!bg-white hover:!text-ink">
                I already have an account
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 max-w-lg gap-6">
              {[
                { k: "GPT-5.5", v: "vision diagnosis" },
                { k: "Open-Meteo", v: "live climate" },
                { k: "1-tap", v: "scan & history" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-display text-2xl">{s.k}</div>
                  <div className="font-mono-data text-white/70">{s.v}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="glass rounded-3xl p-5 shadow-glass">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden relative">
                <img src={LEAF_DETAIL} alt="Leaf with stress spots" className="w-full h-full object-cover" />
                <div className="absolute inset-3 border-2 border-dashed border-white/70 rounded-2xl pointer-events-none" />
                <div className="absolute left-3 right-3 top-3 bottom-3 overflow-hidden rounded-2xl pointer-events-none">
                  <div className="absolute left-0 right-0 h-[2px] bg-ochre/80 shadow-[0_0_20px_5px_rgba(217,160,91,0.55)] animate-scan-sweep" />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-ink">
                <div className="bg-white/70 rounded-xl p-3 border border-sand-200">
                  <div className="font-mono-data text-mute">disorder</div>
                  <div className="font-display text-lg leading-tight">Marginal heat scorch</div>
                </div>
                <div className="bg-white/70 rounded-xl p-3 border border-sand-200">
                  <div className="font-mono-data text-mute">climate risk</div>
                  <div className="font-display text-lg leading-tight text-forest">74<span className="text-sm text-mute">/100</span></div>
                </div>
                <div className="bg-white/70 rounded-xl p-3 border border-sand-200">
                  <div className="font-mono-data text-mute">advice</div>
                  <div className="text-sm leading-tight">Shade-cloth midday, water 6am</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-2 px-4 py-3 rounded-full glass shadow-glass">
              <MapPin size={16} weight="duotone" className="text-forest" />
              <span className="font-mono-data text-ink">geolocated · live</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="font-mono-data text-mute mb-3">how it works</div>
            <h2 className="font-display text-4xl md:text-5xl text-ink max-w-2xl leading-tight">
              Three breaths and you have a diagnosis grounded in <em className="italic text-terra">your</em> climate.
            </h2>
          </div>
          <Link to="/register" className="btn-primary" data-testid="cta-howitworks">Try it now <ArrowRight size={16} weight="bold" /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { Icon: CameraRotate, title: "Capture", body: "Snap a photo or run a 4-second video scan from any phone. Multiple frames make the diagnosis sharper." },
            { Icon: ThermometerHot, title: "Climate-aware", body: "We pull live temperature, humidity, UV and precipitation from Open-Meteo using your location, then fuse it with the image." },
            { Icon: Lightning, title: "Diagnose", body: "GPT-5.5 vision returns the disorder, severity, climate stressors, and a same-day action plan." },
          ].map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-sand-100 border border-sand-200 rounded-3xl p-6 relative overflow-hidden"
            >
              <div className="w-12 h-12 rounded-2xl bg-forest text-white flex items-center justify-center mb-5">
                <s.Icon size={22} weight="duotone" />
              </div>
              <h3 className="font-display text-2xl mb-2">{s.title}</h3>
              <p className="text-mute leading-relaxed">{s.body}</p>
              <div className="absolute right-4 top-4 font-mono-data text-sand-300 text-lg">0{i + 1}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CLIMATE MATTERS */}
      <section className="bg-sand-100 border-y border-sand-200 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="font-mono-data text-mute mb-3">why climate matters</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
              Most "diseases" are really <em className="italic text-terra">climate symptoms</em> in disguise.
            </h2>
            <p className="mt-5 text-mute leading-relaxed">
              Heat scorch reads as fungal blight. Low humidity mimics nutrient burn.
              Without climate context, generic plant apps are guessing — and guesswork
              kills crops, kitchen gardens and houseplants alike.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { k: "+40°C", v: "Heat scorch threshold for most leafy greens" },
              { k: "< 30%", v: "Humidity that triggers tip burn in tropicals" },
              { k: "UV 8+", v: "Sunburn risk for shade-loving species" },
              { k: "> 25mm/day", v: "Waterlogging that breeds root rot" },
            ].map((m) => (
              <div key={m.k} className="bg-white/80 border border-sand-200 rounded-2xl p-5">
                <div className="font-display text-4xl text-forest leading-none">{m.k}</div>
                <div className="text-mute mt-2">{m.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-5xl mx-auto px-6 lg:px-12 text-center">
        <Plant size={48} weight="duotone" className="text-forest mx-auto mb-6" />
        <h2 className="font-display text-4xl md:text-6xl leading-tight text-ink">
          Bring your garden into the conversation about climate.
        </h2>
        <p className="text-mute mt-5 text-lg max-w-2xl mx-auto">
          Free to start. Sign up, point your camera, and we'll handle the science.
        </p>
        <Link to="/register" data-testid="cta-bottom" className="btn-primary mt-8 inline-flex">
          Open the scanner <ArrowRight size={16} weight="bold" />
        </Link>
      </section>

      <footer className="py-10 border-t border-sand-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-between gap-4 text-sm text-mute">
          <div className="flex items-center gap-2">
            <Leaf size={16} weight="duotone" className="text-forest" />
            <span className="font-display text-lg text-ink">A Must For Earth</span>
            <span className="font-mono-data">2026</span>
          </div>
          <div className="flex items-center gap-2"><ChartLineUp size={14} weight="duotone" /> Powered by GPT-5.5 + Open-Meteo</div>
        </div>
      </footer>
    </div>
  );
}
